import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { Indexable } from "../../types/misc";
import { CrfDataApi, CrfExportFilterUI, CrfExportFinishStatusAPI } from "../../types/crf";
import { ApiErrorResponse, EndpointRequest } from "../../api/types/base";
import CrfAPI from "../../api/endpoints/crf";
import { ResponseCode } from "../../api/types/responseCode";
import { showErrorMessage } from "../utils";
import PatientAPI from "../../api/endpoints/patient";
import { removePatientIdPrefix } from "../../utils/testkit";
import { Toast } from "../../components/Toast";
import { SiteData } from "../../types/account";
import { FinishTrialReason } from "../../types/patient";

interface CrfState {
  isLoadingCrf: boolean,
  isUploadingCrf: boolean,
  data?: CrfDataApi,

  isLoadingCrfIds: boolean,
  crfIds?: string[],
}

const initialState: CrfState = {
  isLoadingCrf: false,
  isUploadingCrf: false,
  data: undefined,

  isLoadingCrfIds: false,
  crfIds: undefined,
}

// NOTE: 'state' is only mutable inside 'createSlice'
export const crfSlice = createSlice({
  name: 'crf',
  initialState,
  reducers: {
    clearCrfIds: (state) => {
      state.crfIds = undefined;
    },
    // storeCrfData: (state, action: PayloadAction<any>) => {

    // },
    updateCrf: (state, action: PayloadAction<{ name: string, value: any }>) => {
      const path = action.payload.name.split('.');
      let stateObj = state.data as Indexable;
      let nextObj;
      let i = 0;

      while (i < path.length - 1) {
        nextObj = stateObj[path[i]];

        // Path not found -> add it
        if (!nextObj) {
          stateObj[path[i]] = {};
          nextObj = stateObj[path[i]];
        }

        stateObj = nextObj;
        i++;
      }

      // if (stateObj[path[i]] !== action.payload.value) {
        stateObj[path[i]] = action.payload.value;
      // }
    },
    clearCrfData: (state) => {
      state.data = initialState.data;
    }
  },
  extraReducers: (builder) => {
    // Thunk: getCrfIds
    builder.addCase(getCrfIds.pending, (state, action) => { state.isLoadingCrfIds = true; });
    builder.addCase(getCrfIds.fulfilled, (state, action) => {
      state.isLoadingCrfIds = false;
      
      const patients = action.payload?.data || [];
      state.crfIds = patients.map(kit => removePatientIdPrefix(kit.id));
    });
    builder.addCase(getCrfIds.rejected, (state, action) => {
      state.isLoadingCrfIds = false;
      
      showErrorMessage(action.payload as ApiErrorResponse, { title: 'Failed to retrieve testkit data' });
    });

    // Thunk: getCrfData
    builder.addCase(getCrfData.pending, (state, action) => { state.isLoadingCrf = true; });
    builder.addCase(getCrfData.fulfilled, (state, action) => {
      state.isLoadingCrf = false;
      state.data = action.payload.data;
    });
    builder.addCase(getCrfData.rejected, (state, action) => {
      state.isLoadingCrf = false;

      const error = action.payload as ApiErrorResponse;
      const noDataFound = error.type === 'errorResponse' && error.status === 404 && error.code?.id === ResponseCode.EntityNotFound;

      // ID is valid but has no CRF data yet -> create blank CRF data
      if (noDataFound) {
        state.data = { id: action.meta.arg.id }
      }
      else {
        showErrorMessage(error, { title: 'Failed to retrieve CRF data' });
      }
    });

    builder.addCase(uploadCrfData.pending, (state, action) => { state.isUploadingCrf = true; });
    builder.addCase(uploadCrfData.fulfilled, (state, action) => {
      state.isUploadingCrf = false;
      state.data = action.meta.arg.data;
      
      Toast.success({ title: `Updated CRF data for ${action.meta.arg.data.id}` });
    });
    builder.addCase(uploadCrfData.rejected, (state, action) => {
      state.isUploadingCrf = false;
      showErrorMessage(action.payload as ApiErrorResponse, { title: 'Failed to update CRF data' });
    });

    builder.addCase(exportCrfReport.fulfilled, (state, action) => {
      Toast.info({ title: `The CRF report will be emailed to you soon` });
    });
    builder.addCase(exportCrfReport.rejected, (state, action) => {
      showErrorMessage(action.payload as ApiErrorResponse, { title: 'Failed to request CRF report' });
    });
  }
});

export default crfSlice.reducer;
export const {
  clearCrfIds,
  // storeCrfData,
  updateCrf,
  clearCrfData,
} = crfSlice.actions;

export const getCrfIds = createAsyncThunk('testkit/getCrfIds',
  async ({ signal }: EndpointRequest, { rejectWithValue }) => (
    PatientAPI.getAllPatients({ limit: 200, includeCompleted: true, signal })
    .catch(error => rejectWithValue(error))
));

export const getCrfData = createAsyncThunk('crf/getCrfData',
  async ({ id, signal }: { id: string } & EndpointRequest, { rejectWithValue }) => (
    await CrfAPI.getCrf({ id, signal })         // API success -> thunk payload is ApiResponse
    .catch(error => rejectWithValue(error))     // API failure -> thunk payload is ApiErrorResponse
))

export const uploadCrfData = createAsyncThunk('crf/uploadCrfData',
  async ({ data, signal }: { data: CrfDataApi } & EndpointRequest, { rejectWithValue }) => (
    await CrfAPI.uploadCrf({ id: data.id.replace('CRF-', ''), data, signal })
    .catch(error => rejectWithValue(error))
))

export const exportCrfReport = createAsyncThunk('crf/exportCrfReport',
  async ({ site, status, signal }: {
    site?: SiteData,
    status?: CrfExportFilterUI
  } & EndpointRequest, { rejectWithValue }) => {
    let finishStatus: CrfExportFinishStatusAPI | undefined  = undefined;
    let finishReason: FinishTrialReason | undefined         = undefined;

    switch (status) {
      case CrfExportFilterUI.AnyOngoing:
        finishStatus = CrfExportFinishStatusAPI.NotCompleted;
        break;
      case CrfExportFilterUI.AnyFinished:
        finishStatus = CrfExportFinishStatusAPI.Completed;
        break;
      case CrfExportFilterUI.Birth:
        finishStatus = CrfExportFinishStatusAPI.Completed;
        finishReason = FinishTrialReason.Birth;
        break;
      case CrfExportFilterUI.Miscarriage:
        finishStatus = CrfExportFinishStatusAPI.Completed;
        finishReason = FinishTrialReason.Miscarriage;
        break;
      case CrfExportFilterUI.Left:
        finishStatus = CrfExportFinishStatusAPI.Completed;
        finishReason = FinishTrialReason.Left;
        break;
    }

    return await CrfAPI.exportReport({ site, finishStatus, finishReason, signal })
    .catch(error => rejectWithValue(error));
  })

export const selectIsLoadingCrfIds = (state: RootState) => state.crf.isLoadingCrfIds;
export const selectIsUploadingCrf = (state: RootState) => state.crf.isUploadingCrf;
export const selectCrfIds = (state: RootState) => state.crf.crfIds;
export const selectCrf = (state: RootState) => state.crf.data;
